import React, { useEffect, useState } from 'react';
import styles from 'components/Player/AudioPlayerWrapper/PlayQueue/PlayQueue.module.scss';
import CurrentlyPlaying from 'components/Player/AudioPlayerWrapper/PlayQueue/CurrentlyPlaying';
import NextInQueueList from 'components/Player/AudioPlayerWrapper/PlayQueue/NextInQueueList';
import NextInSeriesList from 'components/Player/AudioPlayerWrapper/PlayQueue/NextInSeriesList';
import NextInRecommendationsList from './NextInRecommendationsList/NextInRecommendationsList';
import { useTranslation } from 'hooks/useTranslation';
import { useTunnusContext } from 'contexts/TunnusContext';
import LoginPromotion from 'components/Player/AudioPlayerWrapper/PlayQueue/LoginPromotion';
import EmptyPlayQueue from 'components/Player/AudioPlayerWrapper/PlayQueue/EmptyPlayQueue';
import { QUEUE_PAGE_SIZE, usePlayerState } from 'contexts/PlayerStateContext';
import { Card } from '@yleisradio/areena-types';
import { DialogTitle } from 'components/Dialog/Dialog';
import { useCards } from 'hooks/useCards';
import { useFeatureFlag } from 'contexts/FeatureFlagContext/FeatureFlagContext';
import { ToggleQueueCardsPrompt } from './ToggleQueueCardsPrompt/ToggleQueueCardsPrompt';
import { idFromPointer } from 'utils/pointer';
import { isString } from 'utils/string';

type UpdateOperation = 'add' | 'remove';

type Props = {
  ongoingCard: Card | undefined;
};

const removeQueueItem = (
  prevState: string[],
  selectedUserQueueCardIndexes: number[]
) => {
  const newState = prevState.filter((_item, index) => {
    return selectedUserQueueCardIndexes.indexOf(index) == -1;
  });
  return newState;
};

const addQueueItem = (
  prevState: string[],
  selectedUserQueueCardIndexes: number[],
  selectedSeriesCardIndexes: number[],
  queue: string[],
  seriesCardIds: string[]
) => {
  const userQueueIds = selectedUserQueueCardIndexes
    .map((i) => queue[i])
    .filter(isString);

  const seriesQueueIds = selectedSeriesCardIndexes
    .map((i) => seriesCardIds[i])
    .filter(isString);

  const newUserQueueState = [...prevState, ...userQueueIds, ...seriesQueueIds];

  return newUserQueueState;
};

export function PlayQueue({ ongoingCard }: Props) {
  const {
    activePlayer,
    queue,
    recommendationsQueue,
    recommendationsAnalyticsContext,
    showRecommendationsQueue,
    setShowRecommendationsQueue,
    setQueue,
  } = usePlayerState();
  const t = useTranslation();
  const { isAuthenticated } = useTunnusContext();
  const { playQueue2024 } = useFeatureFlag();

  const cardsInQueue = Boolean(ongoingCard || queue?.length);
  const {
    cards: seriesCards,
    loading,
    analyticsContext,
  } = useCards({
    source: activePlayer?.playlistSource,
    pageIndex: 0,
    pageSize: QUEUE_PAGE_SIZE,
  });

  const [selectedUserQueueCardIndexes, setSelectedUserQueueCardIndexes] =
    useState<number[]>([]);

  const [selectedSeriesCardIndexes, setSelectedSeriesCardIndexes] = useState<
    number[]
  >([]);

  const [showQueueControlPrompt, setShowQueueControlPrompt] =
    useState<boolean>(false);

  const seriesCardIds = seriesCards.map((card) => {
    const cardId = idFromPointer(card.pointer) || '';
    return cardId;
  });

  /* design decision: if series cards chekboxes are clicked, remove button from prompt
   is disabled for all to keep series queue intact */
  const isRemoveQueueCardsButtonDisabled = selectedSeriesCardIndexes.length > 0;

  useEffect(
    function checkToggleQueueCardsPromptVisibility() {
      const allSelectedQueueCards = [
        ...selectedSeriesCardIndexes,
        ...selectedUserQueueCardIndexes,
      ];
      if (allSelectedQueueCards.length > 0) {
        setShowQueueControlPrompt(true);
      } else {
        setShowQueueControlPrompt(false);
      }
    },
    [selectedUserQueueCardIndexes, selectedSeriesCardIndexes]
  );

  const updatePlayQueueItems = (operationType: UpdateOperation) => {
    setQueue((prevState) => {
      if (operationType == 'remove') {
        return removeQueueItem(prevState, selectedUserQueueCardIndexes);
      }
      if (operationType == 'add') {
        return addQueueItem(
          prevState,
          selectedUserQueueCardIndexes,
          selectedSeriesCardIndexes,
          queue,
          seriesCardIds
        );
      } else {
        return prevState;
      }
    });
    setSelectedSeriesCardIndexes([]); //reset series ui cards
    setSelectedUserQueueCardIndexes([]); //reset user queue ui cards
  };

  const handleUserQueueCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    if (event.target.checked) {
      setSelectedUserQueueCardIndexes([...selectedUserQueueCardIndexes, index]);
    } else {
      setSelectedUserQueueCardIndexes(
        selectedUserQueueCardIndexes.filter((i) => i !== index)
      );
    }
  };

  const handleSeriesQueueCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    if (event.target.checked) {
      setSelectedSeriesCardIndexes([...selectedSeriesCardIndexes, index]);
    } else {
      setSelectedSeriesCardIndexes(
        selectedSeriesCardIndexes.filter((i) => i !== index)
      );
    }
  };

  return (
    <div className={styles.queue}>
      <DialogTitle asChild>
        <h2 className={styles.title}>{t('queue')}</h2>
      </DialogTitle>
      {cardsInQueue && isAuthenticated && (
        <>
          <CurrentlyPlaying />
          {queue.length > 0 && (
            <NextInQueueList
              itemIds={queue}
              handleCheckboxChange={handleUserQueueCheckboxChange}
              selectedCardIndexes={selectedUserQueueCardIndexes}
            />
          )}
          {seriesCards.length > 0 && (
            <NextInSeriesList
              heading={t('nextInSeries')}
              cards={seriesCards}
              analyticsContext={analyticsContext}
              listKey="play-queue-next-in-series"
              loading={loading}
              handleCheckboxChange={handleSeriesQueueCheckboxChange}
              selectedCardIndexes={selectedSeriesCardIndexes}
            />
          )}
          {recommendationsQueue.length > 0 && (
            <NextInRecommendationsList
              heading={t('recommendationsInQueue')}
              cards={recommendationsQueue}
              analyticsContext={recommendationsAnalyticsContext}
              listKey="play-queue-recommendations"
              showRecommendationsQueue={showRecommendationsQueue}
              setShowRecommendationsQueue={setShowRecommendationsQueue}
            />
          )}
          {playQueue2024 && showQueueControlPrompt && (
            <ToggleQueueCardsPrompt
              isRemoveDisabled={isRemoveQueueCardsButtonDisabled}
              updateItems={updatePlayQueueItems}
              setShowQueueControlPrompt={setShowQueueControlPrompt}
            />
          )}
        </>
      )}
      {!cardsInQueue && isAuthenticated && <EmptyPlayQueue />}
      {!isAuthenticated && <LoginPromotion />}
    </div>
  );
}
